import { useCallback, useEffect, useRef } from 'react';

import { Stack, Text } from 'ui';
import { type UseFormReturnType, isNotEmpty, useForm } from 'ui/Form';

import { useFormListValidator } from '../../../..';
import { useShowAccountCodeByCode } from '../../../../api';
import { SearchAccountCodesInput } from '../../inputs/searchable/SearchAccountCodes/SearchAccountCodesInput';

interface AccountCodeSearchSelectProps {
  causeId: string;
  accountCodeId?: string;
  disabled: boolean;
  label?: string;
  /** If true, the account code is required to not be empty */
  validateEmptyAccountCode?: boolean;
  /** The form id to be used for the form that will be accessible on formy
   * @default 'account_code'
   */
  formId?: string;
}

type AccountCodeSearchFormType = {
  accountCode: string;
};

type AccountCodeSearchFormTransformedValues = (values: AccountCodeSearchFormType) => {
  code: string;
  uuid: string;
};

type AccountCodeSearchSelectForm = UseFormReturnType<
  AccountCodeSearchFormType,
  AccountCodeSearchFormTransformedValues
>;

type AccountCodesSearchableSelectItemProps = {
  option: {
    label: string;
    value: string;
    code?: string;
  };
};

const AccountCodesSearchableSelectItem = ({ option }: AccountCodesSearchableSelectItemProps) => (
  <Stack gap="xs" w="100%">
    <Text fz="sm">{option.label}</Text>
    <Text fz={10}>{option.code}</Text>
  </Stack>
);

const AccountCodeSearchSelect = ({
  causeId,
  accountCodeId,
  disabled,
  label = 'Account Code',
  validateEmptyAccountCode,
  formId,
}: AccountCodeSearchSelectProps) => {
  const dataRef = useRef<{ value: string; code: string; label: string }[]>([]);

  /** Get the account code from the dataRef which is a ref to the data of the searchable input */
  const getAccountCode = useCallback((accountCode: string) => {
    if (dataRef.current) {
      const foundItem = dataRef.current.find((item) => {
        if (typeof item === 'object' && 'value' in item) {
          return item?.value === accountCode;
        }
        return item === accountCode;
      });
      if (foundItem) {
        return foundItem?.code;
      }
    }
    return accountCode;
  }, []);

  const form = useForm<AccountCodeSearchFormType, AccountCodeSearchFormTransformedValues>({
    initialValues: {
      accountCode: accountCodeId ?? '',
    },
    validate: {
      accountCode: validateEmptyAccountCode ? isNotEmpty('Account code is required') : undefined,
    },
    transformValues: (values) => {
      return {
        // the actually account code
        code: getAccountCode(values.accountCode),
        // The uuid of the account code
        uuid: values.accountCode,
      };
    },
  });

  useEffect(() => {
    form.setFieldValue('accountCode', accountCodeId ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountCodeId]);

  useFormListValidator({ form, id: formId || 'account_code' });

  return (
    <SearchAccountCodesInput
      dataRef={dataRef}
      comboboxProps={{ withinPortal: true }}
      causeId={causeId}
      label={label}
      placeholder="Select an account code"
      disabled={!!disabled}
      renderOption={AccountCodesSearchableSelectItem}
      filter={(input) =>
        input.options.filter((item) => {
          const option = item as unknown as { label: string; code: string };

          return (
            option.code?.toLowerCase().includes(input.search.toLowerCase()) ||
            option.label?.toLowerCase().includes(input.search.toLowerCase())
          );
        })
      }
      {...form.getInputProps('accountCode')}
    />
  );
};

const AccountCodeSearchSelectWrapper = ({
  causeId,
  accountCode,
  disabled = false,
  ...props
}: {
  causeId: string;
  accountCode?: string | null;
  disabled?: boolean;
  label?: string;
  /** If true, the account code is required to not be empty */
  validateEmptyAccountCode?: boolean;
  formId?: string;
}) => {
  const { data: accountCodeData } = useShowAccountCodeByCode({
    causeId,
    accountCode,
  });

  return (
    <AccountCodeSearchSelect
      causeId={causeId}
      accountCodeId={accountCodeData?.uuid}
      disabled={disabled}
      {...props}
    />
  );
};

export { AccountCodeSearchSelect, AccountCodeSearchSelectWrapper };
export type {
  AccountCodeSearchFormTransformedValues,
  AccountCodeSearchFormType,
  AccountCodeSearchSelectForm,
};
